import { WORKSPACE_TEMPLATE_OPTIONS } from '@/types/workspaceTemplates';

import type { LifecycleTemplateDefinition, LifecycleTemplateId } from './domain';

const workspaceTemplate = (key: (typeof WORKSPACE_TEMPLATE_OPTIONS)[number]['key']) =>
    WORKSPACE_TEMPLATE_OPTIONS.find((template) => template.key === key);

const template = (
    id: LifecycleTemplateId,
    workspaceTemplateKey: (typeof WORKSPACE_TEMPLATE_OPTIONS)[number]['key'],
    taskCategories: LifecycleTemplateDefinition['taskCategories'],
    label?: string,
): LifecycleTemplateDefinition => {
    const source = workspaceTemplate(workspaceTemplateKey);

    return {
        description: source?.description ?? '',
        id,
        label: label ?? source?.label ?? 'Lifecycle',
        taskCategories,
    };
};

export const LIFECYCLE_TEMPLATES: readonly LifecycleTemplateDefinition[] = [
    {
        id: 'blank',
        label: 'Blank Lifecycle',
        description: 'Start with an empty initiative and structure a custom oversight process.',
        taskCategories: [],
    },
    template(
        'asset-manager-ria',
        'assetManagerRia',
        [
            {
                label: 'Investment Oversight',
                description: 'Establish the evidence, ownership, and cadence for ongoing portfolio oversight.',
                taskGroups: [
                    {
                        label: 'Initial Review',
                        tasks: [
                            { title: 'Confirm mandate and restrictions' },
                            { title: 'Attach current investment memo' },
                        ],
                    },
                    {
                        label: 'Recurring Monitoring',
                        tasks: [
                            { title: 'Review performance and attribution' },
                            { title: 'Document mandate exceptions' },
                        ],
                    },
                ],
            },
            {
                label: 'Governance',
                taskGroups: [
                    {
                        label: 'Quarterly Approval',
                        tasks: [{ title: 'Collect reviewer sign-off' }, { title: 'Archive oversight evidence' }],
                    },
                ],
            },
        ],
        'Asset Manager / RIA Review',
    ),
    template(
        'hedge-fund',
        'hedgeFund',
        [
            {
                label: 'Thesis Monitoring',
                taskGroups: [
                    {
                        label: 'Catalysts & Risks',
                        tasks: [{ title: 'Refresh catalyst timeline' }, { title: 'Test thesis-breaking conditions' }],
                    },
                    {
                        label: 'Position Review',
                        tasks: [{ title: 'Review exposure and liquidity' }, { title: 'Record sizing rationale' }],
                    },
                ],
            },
            {
                label: 'Portfolio Oversight',
                taskGroups: [{ label: 'Risk Committee', tasks: [{ title: 'Prepare risk review packet' }] }],
            },
        ],
        'Hedge Fund Thesis Monitoring',
    ),
    template(
        'family-office',
        'familyOffice',
        [
            {
                label: 'Direct Investment Diligence',
                taskGroups: [
                    {
                        label: 'Management & Ownership',
                        tasks: [{ title: 'Complete management references' }, { title: 'Verify ownership structure' }],
                    },
                    {
                        label: 'Evidence Review',
                        tasks: [{ title: 'Reconcile diligence request list' }, { title: 'Review legal documentation' }],
                    },
                ],
            },
            {
                label: 'Stewardship',
                taskGroups: [{ label: 'Family Approval', tasks: [{ title: 'Record investment committee decision' }] }],
            },
        ],
        'Family Office Direct Investment',
    ),
    template(
        'endowment',
        'endowment',
        [
            {
                label: 'Manager DDQ',
                taskGroups: [
                    {
                        label: 'Organization & Strategy',
                        tasks: [{ title: 'Refresh manager DDQ' }, { title: 'Review organizational changes' }],
                    },
                    {
                        label: 'Operational Due Diligence',
                        tasks: [
                            { title: 'Review controls and service providers' },
                            { title: 'Document open exceptions' },
                        ],
                    },
                ],
            },
            {
                label: 'Ongoing Oversight',
                taskGroups: [{ label: 'Annual Review', tasks: [{ title: 'Complete annual manager attestation' }] }],
            },
        ],
        'Endowment Manager Oversight',
    ),
    template(
        'corporate-development',
        'corporateDevelopment',
        [
            {
                label: 'Transaction Diligence',
                taskGroups: [
                    {
                        label: 'Strategic Fit',
                        tasks: [{ title: 'Validate strategic rationale' }, { title: 'Document key transaction risks' }],
                    },
                    {
                        label: 'Confirmatory Diligence',
                        tasks: [{ title: 'Reconcile data room requests' }, { title: 'Review diligence exceptions' }],
                    },
                ],
            },
            {
                label: 'Approval & Close',
                taskGroups: [
                    {
                        label: 'Decision Governance',
                        tasks: [{ title: 'Collect transaction approvals' }, { title: 'Archive decision materials' }],
                    },
                ],
            },
        ],
        'M&A / Corporate Development',
    ),
];

export const getLifecycleTemplate = (templateId: LifecycleTemplateId) =>
    LIFECYCLE_TEMPLATES.find((candidate) => candidate.id === templateId) ?? LIFECYCLE_TEMPLATES[0];
